/**
 * Aviso de acceso por WhatsApp.
 *
 * POR QUÉ WHATSAPP Y NO CORREO: la ventana de aprobación insinuaba desde siempre que las
 * credenciales le llegaban a la persona por correo —tenía un campo «Correo de acceso» y debajo
 * «Clave temporal»— y eso **nunca fue cierto**: el sistema no tiene ni ha tenido forma de enviar
 * un correo. Decisión del usuario del 2026-09-04: WhatsApp, que además es donde la gente de campo
 * sí lee. De los 12 registros del formulario público, los 12 dejaron celular.
 *
 * POR QUÉ DE UN CLIC Y NO AUTOMÁTICO: `wa.me` abre WhatsApp con el mensaje ya escrito y **la
 * persona aprieta enviar**. No cuesta nada y no depende de contratar a nadie. El envío automático
 * exige la API de WhatsApp Business con plantillas aprobadas por Meta, y ahorra dos segundos a
 * cambio de un riesgo real: un mensaje con la clave de acceso conviene mirarlo antes de mandarlo,
 * porque mandárselo al número equivocado no se deshace.
 */

/** Código de país del Perú. El único que maneja la operación hoy. */
const CODIGO_PERU = '51';

/**
 * Celular en el formato que espera `wa.me`: solo dígitos, con código de país.
 *
 * Devuelve `null` cuando el número no sirve, y ahí el botón no se muestra. Mandar a WhatsApp un
 * número inventado abre un chat con un desconocido que recibiría la clave de otra persona.
 */
export function telefonoParaWhatsapp(crudo: string | null | undefined): string | null {
  const digitos = (crudo ?? '').replace(/\D/g, '');
  if (digitos === '') return null;
  // Ya trae el código de país: 51 + 9 dígitos.
  if (digitos.length === 11 && digitos.startsWith(CODIGO_PERU)) return digitos;
  // Celular peruano suelto: 9 dígitos que empiezan en 9.
  if (digitos.length === 9 && digitos.startsWith('9')) return CODIGO_PERU + digitos;
  return null;
}

export interface AvisoDeAcceso {
  nombre: string;
  /** El documento, que es lo que la persona teclea para entrar. */
  documento: string;
  clave: string;
  /** Dirección de la aplicación. Se pasa desde afuera para no fijar el dominio en el código. */
  url: string;
  /**
   * Campaña a la que está asignada, si ya se sabe.
   *
   * Va como opcional a propósito: hoy las 3.018 posiciones del cronograma están ABIERTAS y nadie
   * está asignado, así que para la mayoría todavía no hay campaña. Inventar una línea vacía
   * («Campaña: —») en el mensaje de bienvenida es peor que no ponerla.
   */
  campania?: string | null;
  /**
   * Tienda y horario del PRÓXIMO turno, si tiene uno por delante.
   *
   * Salen de un turno que aún no pasó, nunca de uno viejo: decirle a alguien «tu tienda es Makro
   * Surco» citando un turno de hace tres semanas es información falsa, y en un mensaje de
   * bienvenida es de las que hacen que la persona se presente donde no debe.
   */
  tienda?: string | null;
  /** Formato `09:00 a 18:00`. */
  horario?: string | null;
}

/**
 * El texto del mensaje.
 *
 * Dice **documento**, no correo: el login pide «DNI o correo» y el de campo entra con su
 * documento. Nombrar el correo `<documento>@campo.treid.pe` —que el servidor arma solo— haría que
 * la persona intentara entrar con algo que no necesita saber y que además no puede recordar.
 */
export function textoDeAvisoDeAcceso(a: AvisoDeAcceso): string {
  /** Solo las líneas que de verdad se saben: un «Campaña: —» en un saludo es peor que nada. */
  const dato = (etiqueta: string, valor: string | null | undefined): string[] => {
    const limpio = (valor ?? '').trim();
    return limpio === '' ? [] : [`${etiqueta}: ${limpio}`];
  };
  return [
    `Hola ${a.nombre.trim()}, ya tienes tu acceso al sistema de asistencia de Treid.`,
    ...dato('Campaña', a.campania),
    ...dato('Tienda', a.tienda),
    ...dato('Horario', a.horario),
    '',
    `Ingresa aquí: ${a.url}`,
    `Usuario: tu documento ${a.documento}`,
    `Clave temporal: ${a.clave}`,
    '',
    'Al entrar te va a pedir crear tu propia clave. No compartas esta clave con nadie.',
  ].join('\n');
}

/**
 * Enlace que abre WhatsApp con el mensaje listo, o `null` si el celular no sirve.
 *
 * `api.whatsapp.com/send` en vez de `wa.me`: se comporta igual en el celular y en la computadora
 * abre WhatsApp Web, que es desde donde se aprueba a la gente.
 */
export function enlaceDeAvisoDeAcceso(
  a: AvisoDeAcceso & { telefono: string | null | undefined },
): string | null {
  const numero = telefonoParaWhatsapp(a.telefono);
  if (numero === null) return null;
  const texto = encodeURIComponent(textoDeAvisoDeAcceso(a));
  return `https://api.whatsapp.com/send?phone=${numero}&text=${texto}`;
}
